// Dip-site recording — water source tracking for fire aviation.
//
// Captures water sources (ponds / lakes / rivers / pools / hydrants /
// tank trucks) along with each individual fill event for:
//   - reimbursement of private landowners
//   - legal cover (proof of date, time, volume, exact coordinate)
//   - operational planning (returning pilots find proven sources)
//
// Pairs with the AFF event taxonomy (src/lib/affEvents.ts) — each
// recorded use ALSO emits a fill_start AFF event so the canonical
// wire-format event stream stays consistent with the in-app view.

import { supabase } from './supabase';
import { useAccountContextStore } from '../stores/accountContextStore';
import { logFillStart, type AffSuppressant } from './affEvents';
import { newClientId } from './ids';
import { enqueueWrite, registerReplayHandler, type QueueEntry } from './writeQueue';

// ── Write-queue replay handler ─────────────────────────────────────────────
registerReplayHandler('dip_site_use', async (entry: QueueEntry) => {
  const result = await recordDipSiteUse(entry.payload as RecordUseInput);
  if (!result.ok && result.error === 'supabase not configured') return false;
  if (!result.ok && result.error === 'not signed in') return false;
  // A unique-violation means the row already landed — treat as success.
  return result.ok || result.error?.includes('23505') || false;
});

export type DipSiteSourceType =
  | 'pond' | 'lake' | 'river' | 'pool' | 'hydrant' | 'tank_truck' | 'other';

export type DipSiteOwnershipClass =
  | 'federal' | 'state' | 'tribal' | 'private' | 'unknown';

export type DipSiteReimbursementStatus =
  | 'unknown' | 'not_required' | 'pending' | 'invoiced' | 'paid' | 'waived';

export interface DipSite {
  id: string;
  name: string;
  sourceType: DipSiteSourceType;
  latitude: number;
  longitude: number;
  altM: number | null;
  elevMslFt: number | null;
  ownershipClass: DipSiteOwnershipClass;
  managingAgency: string | null;
  ownerName: string | null;
  parcelId: string | null;
  ownerContact: string | null;
  ownerNotes: string | null;
  accessNotes: string | null;
  hazards: string | null;
  reimbursementStatus: DipSiteReimbursementStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDipSiteInput {
  name?: string;
  sourceType?: DipSiteSourceType;
  latitude: number;
  longitude: number;
  altM?: number;
  elevMslFt?: number;
  accessNotes?: string;
  hazards?: string;
}

export interface RecordUseInput {
  dipSiteId: string;
  gallons?: number;
  liters?: number;
  suppressant?: AffSuppressant;
  flightId?: string;
  flightClientId?: string;
  aircraftClientId?: string;
  incidentId?: string;
  notes?: string;
}

export interface RecordUseResult {
  ok: boolean;
  useId?: string;
  affEventId?: string;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────
// Dip-site lifecycle
// ─────────────────────────────────────────────────────────────────────

/**
 * Create a dip site (the source itself). Idempotent only by id; the
 * caller decides whether to dedupe by proximity to existing sites.
 */
export async function createDipSite(input: CreateDipSiteInput): Promise<{ ok: boolean; id?: string; error?: string }> {
  if (!supabase) return { ok: false, error: 'supabase not configured' };
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'not signed in' };
  const ownerId = user.id;

  const ctx = useAccountContextStore.getState().ctx;
  const orgId = ctx.kind === 'organizational' ? ctx.orgId : null;

  const { data, error } = await supabase
    .schema('app')
    .from('dip_sites')
    .insert({
      owner_id: ownerId,
      org_id: orgId,
      name: input.name ?? defaultDipSiteName(input.latitude, input.longitude),
      source_type: input.sourceType ?? 'pond',
      latitude: input.latitude,
      longitude: input.longitude,
      alt_m: input.altM ?? null,
      elev_msl_ft: input.elevMslFt ?? null,
      access_notes: input.accessNotes ?? null,
      hazards: input.hazards ?? null,
    })
    .select('id')
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[dipSites] create failed:', error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true, id: (data as { id: string }).id };
}

/**
 * Record a use of a dip site (one fill event). Also emits a
 * canonical AFF fill_start event so the wire-format stream stays
 * consistent.
 */
export async function recordDipSiteUse(input: RecordUseInput): Promise<RecordUseResult> {
  if (!supabase) return { ok: false, error: 'supabase not configured' };
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'not signed in' };
  const ownerId = user.id;

  const ctx = useAccountContextStore.getState().ctx;
  const orgId = ctx.kind === 'organizational' ? ctx.orgId : null;

  // Stable client-side ID for idempotency. dip_site_uses has a partial
  // unique constraint on (owner_id, client_id) so retries hitting the
  // same client_id will conflict cleanly instead of creating duplicates.
  // (Constraint: dip_site_uses_owner_client_id_uniq — pre-existing.)
  // The companion AFF event gets a deterministic sibling key so a
  // retry of recordDipSiteUse() is fully idempotent across BOTH rows
  // (dip_site_uses AND the aff_events row logFillStart inserts).
  const clientId = newClientId('dip');
  const affClientId = `${clientId}-aff`;

  // 1. Emit the canonical AFF event first so we can link the row to it.
  const affRes = await logFillStart({
    suppressant: input.suppressant ?? 'water',
    gallons: input.gallons,
    liters: input.liters,
    subject: 'tank',
    flightId: input.flightId,
    incidentId: input.incidentId,
    notes: input.notes ?? `Dip site ${input.dipSiteId}`,
    clientId: affClientId,
  });

  // 2. Insert the use row, linking to the AFF event when we got one.
  const { data, error } = await supabase
    .schema('app')
    .from('dip_site_uses')
    .insert({
      owner_id: ownerId,
      org_id: orgId,
      client_id: clientId,  // idempotency guard via owner+client_id unique index
      dip_site_id: input.dipSiteId,
      // input.flightId is the local client_id (text), not a server
      // uuid. Send null to the legacy uuid column and route through
      // the denormalized text column.
      flight_id: null,
      flight_client_id: input.flightClientId ?? input.flightId ?? null,
      aircraft_client_id: input.aircraftClientId ?? null,
      // incidentId is the local client_id (text), not a server uuid.
      // Use the denormalized text column added in migration 0031.
      incident_id: null,
      incident_client_id: input.incidentId ?? null,
      aff_event_id: affRes.ok ? affRes.id ?? null : null,
      gallons: input.gallons ?? null,
      liters: input.liters ?? null,
      suppressant: input.suppressant ?? 'water',
      notes: input.notes ?? null,
    })
    .select('id')
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[dipSites] use insert failed:', error.message);
    // Write-queue: persist to IDB for offline replay (OAC5).
    // The client_id idempotency key makes replay safe.
    void enqueueWrite({
      id: clientId,
      type: 'dip_site_use',
      enqueuedAt: new Date().toISOString(),
      payload: input,
    });
    return { ok: false, error: error.message };
  }

  return {
    ok: true,
    useId: (data as { id: string }).id,
    affEventId: affRes.ok ? affRes.id : undefined,
  };
}

/**
 * List dip sites visible to the current user (owner + same-org rows).
 */
export async function listDipSites(): Promise<DipSite[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .schema('app')
    .from('dip_sites')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[dipSites] list failed:', error.message);
    return [];
  }
  return (data ?? []).map(rowToDipSite);
}

/**
 * Update ownership / classification fields after a landowner lookup.
 */
export async function updateDipSiteOwnership(
  dipSiteId: string,
  patch: {
    ownershipClass?: DipSiteOwnershipClass;
    managingAgency?: string | null;
    ownerName?: string | null;
    parcelId?: string | null;
    ownerContact?: string | null;
  },
): Promise<{ ok: boolean; error?: string }> {
  if (!supabase) return { ok: false, error: 'supabase not configured' };

  // Auto-set reimbursement_status when classification flips to federal
  // (no reimbursement needed) or private (reimbursement may apply).
  const update: Record<string, unknown> = {};
  if (patch.ownershipClass !== undefined) {
    update.ownership_class = patch.ownershipClass;
    if (patch.ownershipClass === 'federal') update.reimbursement_status = 'not_required';
    else if (patch.ownershipClass === 'private') update.reimbursement_status = 'pending';
  }
  if (patch.managingAgency !== undefined) update.managing_agency = patch.managingAgency;
  if (patch.ownerName      !== undefined) update.owner_name = patch.ownerName;
  if (patch.parcelId       !== undefined) update.parcel_id = patch.parcelId;
  if (patch.ownerContact   !== undefined) update.owner_contact = patch.ownerContact;
  update.updated_at = new Date().toISOString();

  const { error } = await supabase
    .schema('app')
    .from('dip_sites')
    .update(update)
    .eq('id', dipSiteId);

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[dipSites] ownership update failed:', error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────
// Landowner lookup (calls /api/landowner)
// ─────────────────────────────────────────────────────────────────────

export interface LandownerLookupResult {
  ownershipClass: DipSiteOwnershipClass;
  managingAgency: string | null;
  ownerName: string | null;
  parcelId: string | null;
  ownerContact: string | null;
  source: string[];
}

/**
 * Hit /api/landowner. Always resolves; on failure returns 'unknown'.
 *
 * 5 s wall-clock timeout (HI-7 audit 2026-05-22): the SW doesn't
 * proxy cross-origin AGOL/parcel sources, so on 100% packet loss
 * the underlying fetch could pend indefinitely. Fail fast and
 * return 'unknown' so the dip-site form remains responsive.
 */
export async function lookupLandowner(lat: number, lon: number): Promise<LandownerLookupResult> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 5000);
  try {
    const r = await fetch(`/api/landowner?lat=${lat}&lon=${lon}`, { signal: ctrl.signal });
    clearTimeout(timer);
    if (!r.ok) return emptyLookupResult();
    const j = (await r.json()) as {
      ownershipClass: DipSiteOwnershipClass;
      managingAgency: string | null;
      ownerName: string | null;
      parcelId: string | null;
      ownerContact: string | null;
      source: string[];
    };
    return {
      ownershipClass: j.ownershipClass,
      managingAgency: j.managingAgency,
      ownerName:      j.ownerName,
      parcelId:       j.parcelId,
      ownerContact:   j.ownerContact,
      source:         j.source,
    };
  } catch (e) {
    clearTimeout(timer);
    // eslint-disable-next-line no-console
    console.warn('[dipSites] landowner lookup failed:', e);
    return emptyLookupResult();
  }
}

function emptyLookupResult(): LandownerLookupResult {
  return {
    ownershipClass: 'unknown',
    managingAgency: null,
    ownerName:      null,
    parcelId:       null,
    ownerContact:   null,
    source:         [],
  };
}

/**
 * Convenience: create dip site + look up owner + update.
 * Returns the created dip-site id.
 */
export async function createDipSiteWithLookup(
  input: CreateDipSiteInput,
): Promise<{ ok: boolean; id?: string; owner?: LandownerLookupResult; error?: string }> {
  const created = await createDipSite(input);
  if (!created.ok || !created.id) return created;

  const owner = await lookupLandowner(input.latitude, input.longitude);
  await updateDipSiteOwnership(created.id, {
    ownershipClass: owner.ownershipClass,
    managingAgency: owner.managingAgency,
    ownerName:      owner.ownerName,
    parcelId:       owner.parcelId,
    ownerContact:   owner.ownerContact,
  });

  return { ok: true, id: created.id, owner };
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

export function defaultDipSiteName(lat: number, lon: number): string {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `Dip site at ${Math.abs(lat).toFixed(3)}${ns} ${Math.abs(lon).toFixed(3)}${ew}`;
}

function rowToDipSite(r: Record<string, unknown>): DipSite {
  return {
    id:             r.id as string,
    name:           (r.name ?? 'Dip site') as string,
    sourceType:     (r.source_type ?? 'pond') as DipSiteSourceType,
    latitude:       r.latitude as number,
    longitude:      r.longitude as number,
    altM:           (r.alt_m ?? null) as number | null,
    elevMslFt:      (r.elev_msl_ft ?? null) as number | null,
    ownershipClass: (r.ownership_class ?? 'unknown') as DipSiteOwnershipClass,
    managingAgency: (r.managing_agency ?? null) as string | null,
    ownerName:      (r.owner_name ?? null) as string | null,
    parcelId:       (r.parcel_id ?? null) as string | null,
    ownerContact:   (r.owner_contact ?? null) as string | null,
    ownerNotes:     (r.owner_notes ?? null) as string | null,
    accessNotes:    (r.access_notes ?? null) as string | null,
    hazards:        (r.hazards ?? null) as string | null,
    reimbursementStatus: (r.reimbursement_status ?? 'unknown') as DipSiteReimbursementStatus,
    createdAt:      (r.created_at ?? new Date().toISOString()) as string,
    updatedAt:      (r.updated_at ?? new Date().toISOString()) as string,
  };
}
